/**
 * GridWidget — T3.10 catalog entry for contentType 'grid'.
 *
 * A nested grid is just a GridItem whose `nestedGridId` points to another
 * grid in the store. This widget renders that nested grid, inheriting the
 * same preview mode as the parent (passed via contentProps.preview).
 *
 * Sizing: the section block's height in the page grid is owned by grid 0.8's
 * store-registered auto-grow (`configureInteractions({ autoGrow })` in
 * usePageNode) — it re-fits the block on every committed nested-grid change
 * and live-grows it during drags/resizes. No per-widget grow wiring here.
 *
 * The nested grid must already exist in the store (created when the
 * parent PageNode initialised its grid and added this item). If it
 * doesn't exist yet, Grid renders null gracefully.
 */
import { css, html } from "react-strict-dom";
import { colors } from "@duro-app/tokens/tokens/colors.css";
import { easing } from "@duro-app/tokens/tokens/motion.css";
import { useCanvasZoomValue } from "../canvasZoom.js";
import { Grid, InteractionLayer } from "@fredericrous/grid";
import { useAddDrag } from "../AddDragContext.js";
import { useBoundariesRevealed } from "../resizeMode.js";
import type { WidgetProps } from "./types.js";
import { makeRenderWidget } from "./renderWidget.js";
import { sizes } from "@duro-app/tokens/tokens/sizes.css";

// Marching-ants drop highlight, in RSD/StyleX (not a plain-CSS class) so the
// whole widget stays react-strict-dom. `css.keyframes` is web-only (it throws
// on native), which is fine — the native drop affordance lives in
// GridWidget.native.tsx.
const dance = css.keyframes({
  to: { backgroundPosition: "12px 0, -12px 100%, 0 -12px, 100% 12px" },
});

const styles = css.create({
  container: {
    position: "relative",
    width: "100%",
    height: "100%",
    minHeight: sizes.dropZoneMinH,
  },
  // pointerEvents:none so the overlay never intercepts the pointermove that
  // drives elementFromPoint hit-testing during the drag.
  highlight: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    borderRadius: "0.5rem",
    backgroundColor: colors.infoBg,
    backgroundImage: `linear-gradient(90deg, ${colors.accent} 50%, transparent 50%), linear-gradient(90deg, ${colors.accent} 50%, transparent 50%), linear-gradient(0deg, ${colors.accent} 50%, transparent 50%), linear-gradient(0deg, ${colors.accent} 50%, transparent 50%)`,
    backgroundRepeat: "repeat-x, repeat-x, repeat-y, repeat-y",
    backgroundSize: "12px 2px, 12px 2px, 2px 12px, 2px 12px",
    backgroundPosition: "0 0, 0 100%, 0 0, 100% 0",
    animationName: dance,
    animationDuration: "0.5s",
    animationIterationCount: "infinite",
    animationTimingFunction: easing.linear,
    pointerEvents: "none",
  },
});

export function GridWidget({ contentProps }: WidgetProps) {
  const canvasZoom = useCanvasZoomValue();
  const nestedGridId = contentProps?.["nestedGridId"] as string | undefined;
  const preview = contentProps?.["preview"] as boolean | undefined;
  // A palette widget dragged over THIS nested grid resolves overGridId to it
  // (section/page drags target the page grid instead). Show the drop highlight
  // so the section reads as a live drop target — the same affordance a page
  // gets for section drags.
  const { overGridId, dragging } = useAddDrag();
  // "Boundaries" on the selection bar: reveal this grid's splitter strips so
  // the shared-edge verb can be found without a hover (grid 2.2's
  // `splitterReveal` paints them and widens them on coarse pointers).
  const boundaries = useBoundariesRevealed(nestedGridId);

  if (!nestedGridId) return null;

  // Render the nested grid's items through the widget catalog (scoped to the
  // nested grid + this preview mode) so text/image/form widgets mount and are
  // editable — without renderItem, Grid falls back to raw contentProps.content
  // and the widgets never appear.
  const renderItem = makeRenderWidget(nestedGridId, !!preview);
  const isWidgetDropTarget =
    !preview && dragging?.kind === "widget" && overGridId === nestedGridId;

  return (
    <html.div style={styles.container}>
      {!preview && (
        <InteractionLayer>
          {/* grid 0.8.32 opt-ins: shift-click/marquee multi-select (group
              drag), pane-style boundary drags between widgets, and Alt+drag
              a gutter to tune the section's gap (persisted via y-grid's
              config sync). */}
          <Grid
            gridId={nestedGridId}
            preview={false}
            renderItem={renderItem}
            multiSelect
            // Plain drag again: gating this behind Alt left the hover
            // affordance advertising a drag that did nothing, which is worse
            // than the accidental resize it prevented. The real fix is the
            // corner-priority hit areas (2.1) plus moving this verb into the
            // selection toolbar — see docs/plans/2026-07-29-resize-affordances.md.
            lineResize
            gapResize
            // Handle hit areas compensate for canvas zoom, so they stay a
            // constant SCREEN size instead of shrinking to ~5px at 0.45.
            visualScale={canvasZoom}
            // "Boundaries" on the selection bar: grid 2.2 paints the strips
            // and widens them to a finger-sized band on coarse pointers.
            splitterReveal={boundaries ? "always" : "hover"}
          />
        </InteractionLayer>
      )}
      {preview && (
        <Grid gridId={nestedGridId} preview={true} renderItem={renderItem} />
      )}
      {isWidgetDropTarget && <html.div style={styles.highlight} />}
    </html.div>
  );
}
